<template lang="pug">
div(ref='chart')
</template>

<script>
import { defineComponent } from 'vue';
import graphStyleVariables from '@/common/graph-components/graphStyles.module.scss';
import * as d3 from 'd3';
import { deepCloneRawIfValid } from '@/util/utilsMisc';

export default defineComponent({
    name: 'TimeSeriesGraph',

    props: {
        data: {
            type: Array,
            required: true,
        },
        width: {
            type: Number,
            default: 900,
        },
        height: {
            type: Number,
            default: 500,
        },
        yAxisLabel: {
            type: String,
            default: '',
        },
        xAxisLabel: {
            type: String,
            default: '',
        },
        colors: {
            type: Array,
            default: () => [
                graphStyleVariables.graphBarColorRed,
                graphStyleVariables.graphBarColorYellow,
                graphStyleVariables.graphBarColorGreen,
            ],
        },
    },
    computed: {
        dateTimeFormat() {
            if (this.totalWidth > 600 && this.chartDataSorted.length < 15) {
                return d3.timeFormat('%d%H%M %b %y');
            } else {
                return d3.timeFormat('%d %b%y');
            }
        },
    },
    watch: {
        data(newVal, _oldVal) {
            if (newVal) {
                this.createGraph();
            }
        },
    },
    mounted() {
        this.createGraph();
    },
    methods: {
        chartDataSorted() {
            const data = deepCloneRawIfValid(this.data);
            return data.sort((a, b) => d3.ascending(new Date(a.time), new Date(b.time)));
        },
        takeTheLatestReportForEachDate() {
            const data = this.chartDataSorted();
            const dataMap = new Map();
            data.forEach((d) => {
                const date = new Date(d.time).toISOString().substr(0, 10);
                if (dataMap.has(date)) {
                    const existing = dataMap.get(date);
                    if (new Date(d.time) > new Date(existing.time)) {
                        dataMap.set(date, d);
                    }
                } else {
                    dataMap.set(date, d);
                }
            });

            return Array.from(dataMap.values());
        },
        createGraph() {
            d3.select(this.$refs.chart).select('svg').remove();
            const data = this.takeTheLatestReportForEachDate();
            // Define dimensions and margins for the chart
            const margin = { top: 20, right: 10, bottom: 50, left: 80 };
            const width = this.width - margin.left - margin.right;
            const height = this.height - margin.top - margin.bottom;
            const maxDomainValue = d3.max(data, (d) => d.value);
            const colorScale = d3.scaleQuantize().domain([0, maxDomainValue]).range(this.colors);

            // Create a scale for the x-axis (time)
            const x = d3
                .scaleTime()
                .domain([
                    d3.min(data, (d) => new Date(d.time)),
                    d3.timeDay.offset(
                        d3.max(data, (d) => new Date(d.time)),
                        1,
                    ), // add 1 day to max time
                ])
                .range([0, width]);

            // Create a scale for the y-axis (value)
            const y = d3
                .scaleLinear()
                .domain([0, d3.max(data, (d) => d.value)])
                .range([height, 0]);

            // Create the chart container
            const svg = d3
                .select(this.$refs.chart)
                .append('svg')
                .attr('width', width + margin.left + margin.right)
                .attr('height', height + margin.top + margin.bottom)
                .append('g')
                .attr('transform', `translate(${margin.left} ${margin.top})`);

            svg.append('g')
                .classed('horizontal-guide-lines', true)
                .selectAll('line')
                .data(y.ticks())
                .join('line')
                .attr('x1', 0)
                .attr('y1', (d) => y(d))
                .attr('x2', this.width)
                .attr('y2', (d) => y(d));

            // y-axis label
            svg.append('text')
                .classed('xy-labels', true)
                .attr('text-anchor', 'end')
                .attr('y', -70)
                .attr('x', -20)
                .attr('transform', 'rotate(-90, -0, 0)')
                .text(this.yAxisLabel);

            // x-axis label
            svg.append('text')
                .classed('xy-labels', true)
                .attr('text-anchor', 'end')
                .attr('y', height + 35)
                .attr('x', width)
                .text(this.xAxisLabel);

            // Create the bars
            svg.selectAll('.bar')
                .data(data)
                .enter()
                .append('rect')
                .attr('class', 'bar')
                .attr('x', (d) => x(new Date(d.time)))
                .attr('y', (d) => y(d.value))
                .attr('width', 40)
                .attr('height', (d) => height - y(d.value))
                .attr('fill', (d) => colorScale(d.value)); // Use colorScale to determine fill color

            // Create the x-axis
            svg.append('g')
                .attr('transform', 'translate(0,' + height + ')')
                .call(
                    d3
                        .axisBottom(x)
                        .ticks(data.length) // Set the number of ticks based on the length of the data
                        .tickFormat((d) => {
                            // Format the date value using d3.timeFormat()
                            const formatter = d3.timeFormat(this.dateTimeFormat);
                            return formatter(d);
                        })
                        .tickSizeOuter(0),
                );

            // Create the y-axis
            svg.append('g').call(d3.axisLeft(y));
        },
    },
});
</script>

<style lang="scss">
@use './loc-graph.module' as *;

svg.node-occupancy-graph {
    position: relative;
    width: 100%;
    height: 100%;
}

g.bar-graph-bars-group rect {
    fill: $blue-primary;
}

g.horizontal-guide-lines line {
    stroke: $blue-primary;
    stroke-width: 0.5;
}

.xy-labels {
    fill: white;
    font-size: 1em;
}

.node-occupancy-graph-title {
    fill: white;
    font-size: 20px;
}
</style>
